import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    FileSignature,
    Upload,
    Sparkles,
    Trash2,
    Download,
    FileText as FileTextIcon,
    FileType,
    Image as ImageIcon,
    Type,
    Loader2,
    AlertCircle,
    CheckCircle2,
    BookOpen,
    ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { cumplimentarAnexosTour } from "@/tours";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import {
    cumplimentarAnexo,
    listFilledAnexos,
    deleteFilledAnexo,
    downloadFilledAnexoUrl,
} from "@/lib/api";
import { SignAnnexButton } from "@/components/SignAnnexButton";

const ACCEPTED_EXTS = [
    ".pdf",
    ".docx",
    ".doc",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".heic",
    ".heif",
];

const sourceMeta = {
    pdf: { icon: FileTextIcon, label: "PDF", cls: "bg-red-50 text-red-700 border-red-200" },
    docx: { icon: FileType, label: "Word", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    image: { icon: ImageIcon, label: "Imagen", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    text: { icon: Type, label: "Texto", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function CumplimentarAnexos() {
    const tour = useTour("tour:cumplimentar-anexos", cumplimentarAnexosTour, { autoStart: true });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [instructions, setInstructions] = useState("");
    const [pliegoText, setPliegoText] = useState("");
    const [pastedText, setPastedText] = useState("");
    const [file, setFile] = useState(null);
    const fileRef = useRef(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const d = await listFilledAnexos();
            setItems(d.items || []);
        } catch (e) {
            toast.error("No se pudieron cargar los anexos cumplimentados");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const resetForm = () => {
        setTitle("");
        setInstructions("");
        setPliegoText("");
        setPastedText("");
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file && !pastedText.trim()) {
            toast.error("Sube un archivo o pega el texto del anexo");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            if (title.trim()) fd.append("title", title.trim());
            if (instructions.trim()) fd.append("instructions", instructions.trim());
            if (pliegoText.trim()) fd.append("pliego_text", pliegoText.trim());
            if (pastedText.trim()) fd.append("pasted_text", pastedText.trim());
            if (file) fd.append("file", file);
            await cumplimentarAnexo(fd);
            toast.success("Anexo cumplimentado por la IA ✨");
            resetForm();
            await reload();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "No se pudo cumplimentar el anexo. Inténtalo de nuevo.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("¿Eliminar este anexo cumplimentado?")) return;
        try {
            await deleteFilledAnexo(id);
            setItems((prev) => prev.filter((x) => x.id !== id));
            toast.success("Anexo eliminado");
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    const onDownload = async (id, fmt, fileTitle) => {
        try {
            const res = await api.get(`/anexos/cumplimentados/${id}/download/${fmt}`, {
                responseType: "blob",
            });
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safeName = (fileTitle || "anexo").replace(/[^A-Za-z0-9 _.-]/g, "_");
            a.download = `${safeName}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Descarga falló");
        }
    };

    return (
        <Layout>
            <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-6" data-testid="cumplimentar-anexos-page">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-pop shrink-0">
                        <FileSignature size={22} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1">
                        <h1 className="font-display text-3xl sm:text-4xl font-black text-ink-950 tracking-tight">
                            Cumplimentar anexos
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Sube cualquier anexo en blanco (PDF, Word, foto o texto) y la IA lo
                            rellena con los datos de tu perfil de empresa. Descárgalo listo en
                            PDF o Word para firmar y subir a la plataforma.
                        </p>
                        <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-ink-400 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                                <BookOpen size={12} />
                                ¿Aún no has rellenado tu{" "}
                                <Link
                                    to="/app/perfil-empresa"
                                    className="text-brand-600 font-bold hover:underline"
                                    data-testid="link-perfil-empresa"
                                >
                                    perfil de empresa
                                </Link>
                                ? Hazlo primero para mejores resultados.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form
                    onSubmit={onSubmit}
                    className="bg-white rounded-3xl border border-ink-100 shadow-pop p-6 lg:p-8 space-y-5"
                    data-testid="anexo-form"
                >
                    <div className="grid lg:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-ink-500">
                                Título (opcional)
                            </Label>
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Ej: Anexo I — Declaración responsable"
                                className="mt-1.5 h-11 rounded-xl border-ink-200"
                                data-testid="anexo-title"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-ink-500">
                                Archivo del anexo
                            </Label>
                            <div className="mt-1.5">
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept={ACCEPTED_EXTS.join(",")}
                                    onChange={(e) =>
                                        setFile(e.target.files?.[0] || null)
                                    }
                                    className="block w-full text-sm font-medium file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-bold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 text-ink-500"
                                    data-testid="anexo-file"
                                />
                                <div className="text-[11px] text-ink-400 font-medium mt-1">
                                    PDF · Word · JPG / PNG (OCR con IA) — máx. 15 MB
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-ink-500">
                            …o pega el texto del anexo
                            <span className="text-ink-400 font-normal lowercase ml-1">
                                (útil si tu PDF está protegido)
                            </span>
                        </Label>
                        <Textarea
                            value={pastedText}
                            onChange={(e) => setPastedText(e.target.value)}
                            placeholder="Pega aquí el texto literal del anexo en blanco, con sus líneas de puntos, casillas, etc."
                            className="mt-1.5 min-h-[120px] rounded-xl border-ink-200 font-mono text-sm"
                            data-testid="anexo-pasted"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-ink-500">
                            Instrucciones para la IA
                            <span className="text-ink-400 font-normal lowercase ml-1">
                                (opcional pero recomendado)
                            </span>
                        </Label>
                        <Textarea
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            placeholder='Ej: "La empresa es española, no marques la cláusula de empresas extranjeras." · "Indica que ofertamos 124.500€ IVA excluido." · "El plazo de ejecución es 24 meses."'
                            className="mt-1.5 min-h-[90px] rounded-xl border-ink-200 text-sm"
                            data-testid="anexo-instructions"
                        />
                    </div>

                    <details className="rounded-xl bg-ink-50/50 border border-ink-100 p-4 group">
                        <summary className="cursor-pointer font-bold text-sm text-ink-700 flex items-center gap-2">
                            <BookOpen size={14} className="text-brand-600" />
                            Añadir contexto del pliego{" "}
                            <span className="text-ink-400 font-normal text-xs">
                                (recomendado si la IA necesita conocer el objeto del contrato)
                            </span>
                        </summary>
                        <Textarea
                            value={pliegoText}
                            onChange={(e) => setPliegoText(e.target.value)}
                            placeholder="Pega aquí el resumen del pliego (objeto, presupuesto, plazo, órgano de contratación…). Máx. 4000 caracteres."
                            className="mt-3 min-h-[100px] rounded-xl border-ink-200 text-sm bg-white"
                            data-testid="anexo-pliego"
                        />
                    </details>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                            type="submit"
                            disabled={submitting || (!file && !pastedText.trim())}
                            className="rounded-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-12 px-6 font-bold shadow-pop hover:scale-[1.02] transition-all disabled:opacity-50"
                            data-testid="anexo-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={16} className="animate-spin mr-2" />
                                    Cumplimentando con IA…
                                </>
                            ) : (
                                <>
                                    <Sparkles size={16} className="mr-2" />
                                    Cumplimentar con IA
                                </>
                            )}
                        </Button>
                    </div>
                </form>

                {/* List of filled annexes */}
                <div className="bg-white rounded-3xl border border-ink-100 shadow-pop p-6 lg:p-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-xl font-black text-ink-950">
                            Anexos cumplimentados
                        </h2>
                        <span className="text-xs font-bold text-ink-500">
                            {items.length} {items.length === 1 ? "anexo" : "anexos"}
                        </span>
                    </div>
                    {loading ? (
                        <div className="text-center text-ink-400 py-12">Cargando…</div>
                    ) : items.length === 0 ? (
                        <div
                            className="text-center py-12"
                            data-testid="anexos-empty"
                        >
                            <FileSignature
                                className="mx-auto text-ink-300 mb-3"
                                size={32}
                                strokeWidth={1.5}
                            />
                            <div className="font-display text-lg font-bold mb-1">
                                Aún no has cumplimentado ningún anexo
                            </div>
                            <div className="text-sm text-ink-500 font-medium">
                                Sube uno arriba y la IA lo rellenará automáticamente.
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {items.map((it) => {
                                const meta = sourceMeta[it.source_kind] || sourceMeta.pdf;
                                const Icon = meta.icon;
                                return (
                                    <div
                                        key={it.id}
                                        className="group border border-ink-100 rounded-2xl p-4 hover:border-amber-200 hover:bg-amber-50/30 transition-all"
                                        data-testid={`anexo-item-${it.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 flex items-center justify-center shrink-0">
                                                <Icon size={18} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <span
                                                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.cls}`}
                                                    >
                                                        {meta.label}
                                                    </span>
                                                    {it.tender_id && (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-200">
                                                            Licitación
                                                        </span>
                                                    )}
                                                    {it.signed_pdf_storage_path && (
                                                        <span
                                                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                            title={
                                                                it.signer_dn
                                                                    ? `Firmado por: ${it.signer_dn}`
                                                                    : "Anexo firmado electrónicamente"
                                                            }
                                                            data-testid={`anexo-badge-signed-${it.id}`}
                                                        >
                                                            Firmado · PAdES
                                                        </span>
                                                    )}
                                                    <span className="text-[11px] text-ink-400 font-mono">
                                                        {it.created_at?.slice(0, 10)}
                                                    </span>
                                                </div>
                                                <div className="font-bold text-ink-950 truncate">
                                                    {it.title || "Anexo"}
                                                </div>
                                                {it.instructions && (
                                                    <div className="text-xs text-ink-500 italic mt-1 line-clamp-1">
                                                        “{it.instructions}”
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <SignAnnexButton
                                                    annexId={it.id}
                                                    isSigned={Boolean(it.signed_pdf_storage_path)}
                                                    signerDn={it.signer_dn}
                                                    compact
                                                    onSigned={(res) => {
                                                        setItems((prev) =>
                                                            prev.map((x) =>
                                                                x.id === it.id
                                                                    ? {
                                                                          ...x,
                                                                          signed_pdf_storage_path:
                                                                              "set",
                                                                          signed_at:
                                                                              res.signed_at,
                                                                          signer_dn:
                                                                              res.signer_dn,
                                                                      }
                                                                    : x
                                                            )
                                                        );
                                                    }}
                                                />
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        onDownload(it.id, "pdf", it.title)
                                                    }
                                                    className="rounded-full h-9 px-3 border-ink-200 font-bold text-xs gap-1.5"
                                                    data-testid={`anexo-dl-pdf-${it.id}`}
                                                >
                                                    <Download size={12} /> PDF
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        onDownload(it.id, "docx", it.title)
                                                    }
                                                    className="rounded-full h-9 px-3 border-ink-200 font-bold text-xs gap-1.5"
                                                    data-testid={`anexo-dl-docx-${it.id}`}
                                                >
                                                    <Download size={12} /> Word
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => onDelete(it.id)}
                                                    className="rounded-full h-9 w-9 p-0 text-ink-400 hover:text-red-600"
                                                    data-testid={`anexo-delete-${it.id}`}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}
